// Firebase configuration and initialization
import { initializeApp } from 'firebase/app';
import { getAuth, setPersistence, browserLocalPersistence, inMemoryPersistence, connectAuthEmulator } from 'firebase/auth';
import { getFirestore, connectFirestoreEmulator } from 'firebase/firestore';
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions';
import { getStorage, connectStorageEmulator } from 'firebase/storage';
import { initializeAppCheck, ReCaptchaEnterpriseProvider } from 'firebase/app-check';
import { getAnalytics, isSupported as isAnalyticsSupported, type Analytics } from 'firebase/analytics';
import { getPerformance, type FirebasePerformance } from 'firebase/performance';
import { getRemoteConfig, fetchAndActivate, isSupported as isRemoteConfigSupported, type RemoteConfig } from 'firebase/remote-config';

// Determine which config to use based on build environment
let firebaseConfig;

// Helper to safely get environment variables
// Works in both Vite/Browser and Jest/Node environments
const getEnvVar = (key: string): string | undefined => {
  // Check import.meta.env first (Vite/Browser environment)
  // Vite replaces import.meta.env.* at build time
  if (typeof import.meta !== 'undefined' && import.meta.env) {
    const value = import.meta.env[key];
    if (value) return value;
  }

  // Fall back to process.env (Jest/Node environment)
  if (typeof process !== 'undefined' && process.env) {
    return process.env[key];
  }

  return undefined;
};

const isProductionHostname = typeof window !== 'undefined' && (
  window.location.hostname === 'canvasestate.com.au' ||
  window.location.hostname === 'www.canvasestate.com.au' ||
  window.location.hostname === 'canvasestate.com' ||
  window.location.hostname === 'www.canvasestate.com' ||
  window.location.hostname === 'financial-portfolio-dash.web.app'
);

if (typeof window !== 'undefined' && (window as any).__FIREBASE_CONFIG__) {
  // Production: Use inline config injected by build script
  firebaseConfig = { ...(window as any).__FIREBASE_CONFIG__ };

  // Ensure authDomain is dynamic for multi-domain support (canvasestate.com vs .com.au)
  if (isProductionHostname && !window.location.hostname.includes('web.app') && !window.location.hostname.includes('firebaseapp.com')) {
    firebaseConfig.authDomain = window.location.hostname.includes('canvasestate.com')
      ? window.location.hostname
      : "canvasestate.com.au";
  }

  console.log('🔥 Using inline Firebase config for production (AuthDomain synced)');
} else if (isProductionHostname) {
  // Production Fallback: Force correct config if __FIREBASE_CONFIG__ is missing
  firebaseConfig = {
    apiKey: "AIzaSyBTMD_gx7Fgc8o4y8uUFsun2KFttvL5YSo",
    appId: "1:713197908614:web:444fa6f5d8577fd884d707",
    authDomain: window.location.hostname.includes('canvasestate.com') ? window.location.hostname : "canvasestate.com.au",
    projectId: "financial-portfolio-dash",
    storageBucket: "financial-portfolio-dash.firebasestorage.app",
    messagingSenderId: "713197908614",
    measurementId: "G-PFMLYDWTRL"
  };
  console.log('🔥 Using HARDCODED Firebase config for production (Fallback)');
} else {
  // Development/Test: Use environment variables
  firebaseConfig = {
    apiKey: getEnvVar('NX_FIREBASE_API_KEY') as string,
    authDomain: getEnvVar('NX_FIREBASE_AUTH_DOMAIN') as string,
    projectId: getEnvVar('NX_FIREBASE_PROJECT_ID') as string,
    storageBucket: getEnvVar('NX_FIREBASE_STORAGE_BUCKET') as string,
    messagingSenderId: getEnvVar('NX_FIREBASE_MESSAGING_SENDER_ID') as string,
    appId: getEnvVar('NX_FIREBASE_APP_ID') as string
  };

  if (!firebaseConfig.projectId) {
    throw new Error('Firebase configuration not found. Please check build configuration.');
  }

  console.log('🔥 Using environment variable Firebase config for development/test');
}

// Initialize Firebase
export const app = initializeApp(firebaseConfig);

console.log('🔥 Firebase app initialized with config:', {
  projectId: firebaseConfig.projectId,
  authDomain: firebaseConfig.authDomain,
});

// App Check Configuration
// Detect environment based on Firebase project ID
export const isDevEnvironment = firebaseConfig.projectId === 'fp-dash-dev';
export const isProdEnvironment = firebaseConfig.projectId === 'financial-portfolio-dash';
const isLocalhost = typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

// Initialize App Check
// Uses debug token for localhost, ReCAPTCHA Enterprise for deployed environments
const isTest = (typeof import.meta !== 'undefined' && import.meta.env?.MODE === 'test') ||
  (typeof process !== 'undefined' && process.env?.['NODE_ENV'] === 'test');

// Check if we're on the development Firebase Hosting domain
const isDevHosting = typeof window !== 'undefined' && (
  window.location.hostname === 'fp-dash-dev.web.app' ||
  window.location.hostname === 'fp-dash-dev.firebaseapp.com'
);

// Check if we're on the production Firebase Hosting domain
const isProdHosting = typeof window !== 'undefined' && (
  window.location.hostname === 'financial-portfolio-dash.web.app' ||
  window.location.hostname === 'financial-portfolio-dash.firebaseapp.com'
);

// Check if we're on the custom domain
const isCustomDomain = typeof window !== 'undefined' && (
  window.location.hostname === 'canvasestate.com.au' ||
  window.location.hostname === 'www.canvasestate.com.au'
);

// Log environment detection for debugging
console.log('🔍 Environment detection:', {
  projectId: firebaseConfig.projectId,
  hostname: typeof window !== 'undefined' ? window.location.hostname : 'N/A',
  isDevEnvironment,
  isProdEnvironment,
  isLocalhost,
  isDevHosting,
  isTest
});

// App Check Configuration:
// - Production: reCAPTCHA Enterprise
// - Development/localhost: enabled with registered debug tokens
const shouldEnableAppCheck = !isTest && (isProdEnvironment || isDevEnvironment || isLocalhost);

// CRITICAL: Set debug token SYNCHRONOUSLY before App Check initialization
// This must happen before initializeAppCheck is called
if (isLocalhost && shouldEnableAppCheck) {
  // @ts-ignore - Setting debug token for localhost
  self.FIREBASE_APPCHECK_DEBUG_TOKEN = 'D70E8703-B0DC-4000-9739-A328363B40F9';
  console.log('🔧 App Check: Debug token set SYNCHRONOUSLY for localhost');
}

if (isDevHosting && shouldEnableAppCheck) {
  // @ts-ignore - Using pre-registered App Check debug token for dev hosting
  self.FIREBASE_APPCHECK_DEBUG_TOKEN = '947FEAAB-5DDD-4220-B7EA-24DCB279C26E';
  console.log('🔧 App Check: Debug token set SYNCHRONOUSLY for dev hosting');
}

console.log('🔐 App Check decision:', {
  shouldEnableAppCheck,
  projectId: firebaseConfig.projectId,
  isDevEnvironment,
  isDevHosting,
  isLocalhost,
  isProdEnvironment,
  hostname: typeof window !== 'undefined' ? window.location.hostname : 'N/A'
});

if (shouldEnableAppCheck) {
  // App Check needs storage (IndexedDB) for token caching. 
  // If storage is blocked, the SDK will log warnings, but we still want to initialize 
  // it so that requests are signed with an App Check token if possible.
  (async () => {
    try {
      // Use the same support check as Remote Config
      const storageSupported = await isRemoteConfigSupported();

      if (!storageSupported) {
        console.warn('🛡️ App Check: Storage (IndexedDB) NOT supported. App Check will initialize with in-memory tokens.');
      }

      // Use reCAPTCHA Enterprise for both dev and production
      const recaptchaSiteKey = isProdEnvironment
        ? '6LdYXz4sAAAAABNlWyvvU22ICzn80D9wQaUkifty'  // Production
        : '6LeaxkAsAAAAAP65FGcCQCnc2pcRHI1UwbWWVL0d'; // Dev

      initializeAppCheck(app, {
        provider: new ReCaptchaEnterpriseProvider(recaptchaSiteKey),
        isTokenAutoRefreshEnabled: true,
      });

      const environment = isLocalhost ? 'LOCALHOST (debug token)' :
        isDevHosting ? 'DEV HOSTING (reCAPTCHA Enterprise)' :
          isProdEnvironment ? 'PRODUCTION (reCAPTCHA Enterprise)' : 'UNKNOWN';
      console.log(`🛡️ App Check initialized for ${environment}`);
    } catch (err) {
      console.warn('⚠️ App Check initialization failed:', err);
    }
  })();
} else {
  console.log('🚫 App Check disabled (test mode)');
}


// Initialize Firebase services
// Auth is initialized AFTER App Check to ensure requests include the token
export const auth = getAuth(app);
export const db = getFirestore(app);
export const functions = getFunctions(app, 'us-central1');
export const storage = getStorage(app);

// Connect to emulators if running on localhost
if (isLocalhost) {
  console.log('🔌 Connecting to Firebase Emulators...');
  try {
    connectAuthEmulator(auth, 'http://localhost:9099', { disableWarnings: true });
    connectFirestoreEmulator(db, 'localhost', 8080);
    connectFunctionsEmulator(functions, 'localhost', 5001);
    connectStorageEmulator(storage, 'localhost', 9199);
    console.log('✅ Connected to local emulators');
  } catch (error) {
    console.warn('⚠️ Failed to connect to emulators:', error);
  }
}

// Initialize Analytics (conditionally - may not be supported in all environments)
export let analytics: Analytics | null = null;

if (!isTest && firebaseConfig.measurementId) {
  isAnalyticsSupported().then(supported => {
    if (supported) {
      analytics = getAnalytics(app);
      console.log('📊 Firebase Analytics initialized with measurement ID:', firebaseConfig.measurementId);
    } else {
      console.warn('⚠️ Firebase Analytics not supported in this environment');
    }
  }).catch(err => {
    console.warn('⚠️ Analytics support check failed:', err);
  });
} else if (!firebaseConfig.measurementId) {
  console.log('⚠️ No measurementId configured - Analytics disabled');
}

// Initialize Performance Monitoring (only in production builds, not localhost)
let performanceInstance: FirebasePerformance | null = null;
if (!isTest && typeof window !== 'undefined') {
  const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  if (!isLocalhost) {
    try {
      performanceInstance = getPerformance(app);
      console.log('⚡ Firebase Performance Monitoring initialized');
    } catch (err) {
      console.warn('⚠️ Performance Monitoring initialization failed:', err);
    }
  } else {
    console.log('⚡ Performance Monitoring skipped (localhost)');
  }
}
export const performance = performanceInstance;

// Initialize Remote Config with defaults - wrapped in a safer way to prevent unhandled rejections
let remoteConfigInstance: RemoteConfig | null = null;
if (!isTest && typeof window !== 'undefined') {
  // Use IIFE to handle the async support check without blocking exports
  (async () => {
    try {
      const supported = await isRemoteConfigSupported();
      if (!supported) {
        console.warn('🔧 Remote Config NOT supported in this environment (storage blocked?)');
        return;
      }

      remoteConfigInstance = getRemoteConfig(app);

      // Set cache time (in production: 1 hour, in dev: 5 minutes)
      remoteConfigInstance.settings.minimumFetchIntervalMillis = isProdEnvironment ? 3600000 : 300000;

      // Set default config values
      remoteConfigInstance.defaultConfig = {
        enable_ai_advice: true,
        enable_market_news: true,
        enable_document_upload: true,
        default_vacancy_rate: 0.04,
        default_management_fee: 0.08,
        show_beta_features: false,
        maintenance_mode: false,
      };

      // Fetch and activate config
      await fetchAndActivate(remoteConfigInstance);
      console.log('🔧 Remote Config fetched and activated');
    } catch (err) {
      console.error('❌ Remote Config initialization failed:', err);
      remoteConfigInstance = null;
    }
  })();
}
export const remoteConfig = remoteConfigInstance;

// Set auth persistence to LOCAL (survives browser refreshes and restarts)
// Note: browserLocalPersistence uses IndexedDB as primary storage
// IMPORTANT: We export this promise so AuthContext can wait for it before proceeding

// Debug object to expose persistence state
const DEBUG: { persistenceType: string; persistenceError: string | null; persistenceReady: boolean } = {
  persistenceType: 'unknown',
  persistenceError: null,
  persistenceReady: false
};
if (typeof window !== 'undefined') {
  (window as any).FIREBASE_DEBUG = DEBUG;
}

export const authPersistenceReady: Promise<void> = (async () => {
  console.log('🔐 [PERSISTENCE] Starting persistence setup...');

  if (isTest) {
    DEBUG.persistenceType = 'test-skip';
    DEBUG.persistenceReady = true;
    return; // Skip persistence setup in tests
  }

  try {
    console.log('🔐 [PERSISTENCE] Attempting LOCAL/IndexedDB persistence...');
    await setPersistence(auth, browserLocalPersistence);
    DEBUG.persistenceType = 'LOCAL';
    DEBUG.persistenceReady = true;
    console.log('✅ [PERSISTENCE] SUCCESS - Using LOCAL/IndexedDB (sessions WILL persist on refresh)');
  } catch (error: any) {
    DEBUG.persistenceError = error.message;
    console.warn('⚠️ [PERSISTENCE] LOCAL failed:', error.message);
    console.warn('⚠️ [PERSISTENCE] Trying IN_MEMORY fallback...');

    // Track this issue in analytics
    import('./analytics').then(({ trackAuthStorageIssue }) => {
      trackAuthStorageIssue('persistence_fallback', {
        errorMessage: error.message,
        fallbackType: 'in_memory'
      });
    }).catch(() => { });

    try {
      await setPersistence(auth, inMemoryPersistence);
      DEBUG.persistenceType = 'IN_MEMORY';
      DEBUG.persistenceReady = true;
      console.warn('⚠️ [PERSISTENCE] Using IN_MEMORY - Sessions will NOT persist on refresh!');
      console.warn('⚠️ This means you WILL be logged out when you refresh the page.');
    } catch (fallbackError: any) {
      DEBUG.persistenceType = 'FAILED';
      DEBUG.persistenceError = fallbackError?.message || 'All persistence failed';
      console.error('❌ [PERSISTENCE] ALL OPTIONS FAILED:', fallbackError);
      import('./analytics').then(({ trackAuthStorageIssue }) => {
        trackAuthStorageIssue('indexeddb_error', {
          errorMessage: fallbackError?.message || 'All persistence failed'
        });
      }).catch(() => { });
    }
  }

  // Log summary
  console.log('🔐 [PERSISTENCE] Setup complete:', DEBUG);
})();

// Simple environment detection based on which config source we used
const isWindowConfigUsed = typeof window !== 'undefined' && !!(window as any).__FIREBASE_CONFIG__;

if (!isTest) {
  console.log(`Firebase initialized for project: ${firebaseConfig.projectId}`);
}

